// Thin shared layer for browser-side Strapi Content API calls. Every call site
// (auth in AuthContext, the editor pages) used to inline the base URL, the
// Bearer/Content-Type headers, the `!response.ok` throw, the `json.data`
// unwrap, and the `error.error.message` parsing. This centralizes all of that.

const apiUrl = process.env.GATSBY_STRAPI_API_URL;

interface StrapiFetchOptions {
  token?: string | null;
  method?: string;
  // JSON-stringified into the request body; also sets the Content-Type header.
  body?: unknown;
  // true (default) → returns json.data; false → the raw parsed JSON (used by
  // the auth endpoints, which return { jwt, user } / the user object directly).
  unwrap?: boolean;
  // Prepended to the thrown error message (e.g. "Saving failed: ").
  errorPrefix?: string;
  // Used when the response body carries no error.message.
  errorFallback?: string;
  signal?: AbortSignal;
}

// Turn a failed Strapi response into an Error, using its `{ error: { message } }`
// body when present, otherwise the given fallback, otherwise the HTTP status.
export async function parseStrapiError(
  response: Response,
  { prefix, fallback }: { prefix?: string; fallback?: string } = {},
): Promise<Error> {
  const json = await response.json().catch(() => null);
  const message = json?.error?.message || fallback || "HTTP " + response.status;
  return new Error((prefix ?? "") + message);
}

// One fetch wrapper for every browser-side Strapi call.
export async function strapiFetch(
  path: string,
  options: StrapiFetchOptions = {},
): Promise<any> {
  const {
    token,
    method = "GET",
    body,
    unwrap = true,
    errorPrefix,
    errorFallback,
    signal,
  } = options;

  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${apiUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!response.ok) {
    throw await parseStrapiError(response, {
      prefix: errorPrefix,
      fallback: errorFallback,
    });
  }

  // Some responses have no body (e.g. a 204 from certain DELETEs).
  if (response.status === 204) {
    return null;
  }
  const json = await response.json().catch(() => null);
  return unwrap ? (json?.data ?? null) : json;
}

// Media URLs in API responses are host-relative (/uploads/...); make them
// absolute for use in the browser.
export function strapiMediaUrl(url: string): string {
  return url.startsWith("/") ? `${apiUrl}${url}` : url;
}

// Sets a shape's or part's preview image via the dedicated thumbnail
// endpoints (Strapi's generic /api/upload is not exposed to users). The
// server accepts a single PNG (magic-byte checked), at most 1000x1000 and
// 5 MB, links it to the draft row, and deletes the replaced file once
// nothing references it anymore. Returns the updated draft row. The image
// is passed as a data URL (e.g. from canvas.toDataURL()).
async function strapiSetThumbnail(
  collection: "shapes" | "parts",
  documentId: string,
  imageDataUrl: string,
  { token, errorPrefix }: { token?: string | null; errorPrefix?: string } = {},
): Promise<any> {
  const imageBlob = await (await fetch(imageDataUrl)).blob();

  const formData = new FormData();
  formData.append("file", imageBlob, "thumbnail.png");

  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    `${apiUrl}/api/${collection}/${documentId}/thumbnail`,
    {
      method: "POST",
      headers,
      body: formData,
    },
  );

  if (!response.ok) {
    throw await parseStrapiError(response, { prefix: errorPrefix });
  }

  const json = await response.json().catch(() => null);
  return json?.data ?? null;
}

export function strapiSetShapeThumbnail(
  shapeDocumentId: string,
  imageDataUrl: string,
  options: { token?: string | null; errorPrefix?: string } = {},
): Promise<any> {
  return strapiSetThumbnail("shapes", shapeDocumentId, imageDataUrl, options);
}

export function strapiSetPartThumbnail(
  partDocumentId: string,
  imageDataUrl: string,
  options: { token?: string | null; errorPrefix?: string } = {},
): Promise<any> {
  return strapiSetThumbnail("parts", partDocumentId, imageDataUrl, options);
}
